import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  MapPin, Shield, BarChart3, Bell, Clock,
  CheckCircle, ArrowRight, Package, Truck, Building2,
  Zap, Globe, Lock, Send, Loader2, ScanBarcode,
  Waves, ThermometerSnowflake, Box, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { LogoIcon } from '@/components/Logo';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Landing = () => {
  const navigate = useNavigate();
  const [isInterestDialogOpen, setIsInterestDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [interestForm, setInterestForm] = useState({
    company: '',
    contact: '',
    email: '',
    palletCount: '',
    message: ''
  });

  const handleInterestSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/interest`, {
        company: interestForm.company,
        contact: interestForm.contact,
        email: interestForm.email,
        pallet_count: interestForm.palletCount,
        message: interestForm.message
      });
      toast.success('Thank you! Our team will contact you shortly.');
      setIsInterestDialogOpen(false);
      setInterestForm({ company: '', contact: '', email: '', palletCount: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md z-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <LogoIcon size={40} />
              <span className="text-xl font-bold text-slate-900">SmartPallet</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('features')} className="text-slate-600 hover:text-slate-900 text-sm font-medium">Features</button>
              <button onClick={() => scrollToSection('industries')} className="text-slate-600 hover:text-slate-900 text-sm font-medium">Industries</button>
              <button onClick={() => scrollToSection('pallet-pool')} className="text-slate-600 hover:text-slate-900 text-sm font-medium">For Asset Owners</button>
              <Button 
                onClick={() => navigate('/login')}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
                data-testid="platform-access-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                Platform Access
              </Button>
            </div>
            {/* Mobile menu */}
            <div className="md:hidden">
              <Button 
                onClick={() => navigate('/login')}
                size="sm"
                variant="outline"
                className="border-blue-600 text-blue-600"
                data-testid="mobile-access-btn"
              >
                <Lock className="w-4 h-4 mr-1" /> Access
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-6">
                <LogoIcon size={18} />
                Value Chain Visibility
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
                Track Every Asset Across Your
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500"> Entire Value Chain</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                SmartPallet gives you real-time visibility into every shipment — from factory to customer. 
                Whether it's fresh salmon, household appliances, or industrial components, 
                know exactly where your assets are and what condition they're in.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={() => scrollToSection('industries')}
                  size="lg"
                  className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-lg px-8"
                  data-testid="see-industries-btn"
                >
                  See Industries
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button 
                  onClick={() => scrollToSection('features')}
                  variant="outline"
                  size="lg"
                  className="text-lg px-8"
                >
                  Learn More
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white rounded-3xl shadow-2xl p-8 border border-slate-200">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-bold text-slate-900">Live Tracking</h3>
                  <span className="flex items-center gap-2 text-green-600 text-sm">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    3 assets active
                  </span>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <MapPin className="w-6 h-6 text-blue-600" />
                      <div>
                        <span className="text-slate-900 font-semibold text-sm block">Salmon Shipment</span>
                        <span className="text-slate-500 text-xs">Hilversum → Langhus</span>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-blue-600">In Transit</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Package className="w-6 h-6 text-green-600" />
                      <div>
                        <span className="text-slate-900 font-semibold text-sm block">Electronics Shipment</span>
                        <span className="text-slate-500 text-xs">Hamburg → Oslo</span>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-green-600">Delivered</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <Box className="w-6 h-6 text-purple-600" />
                      <div>
                        <span className="text-slate-900 font-semibold text-sm block">Industrial Parts</span>
                        <span className="text-slate-500 text-xs">Rotterdam → Stavanger</span>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-purple-600">~2h 30min</span>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-slate-200">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    All shipments on schedule — 0 alerts
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-2xl opacity-20 blur-xl"></div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-br from-purple-400 to-pink-400 rounded-2xl opacity-20 blur-xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Complete Supply Chain Visibility
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              IoT-enabled tracking that adapts to your industry — from perishable goods 
              requiring temperature monitoring to high-value assets needing location security
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: MapPin,
                title: 'Real-Time GPS Tracking',
                description: 'Know exactly where every asset is at any moment. Multi-method positioning via GPS, WiFi, and cell towers.',
                color: 'text-blue-600',
                bg: 'bg-blue-100'
              },
              {
                icon: Eye,
                title: 'Condition Monitoring',
                description: 'Temperature, shock, and motion sensors. Optional per use case — enable only what matters for your goods.',
                color: 'text-green-600',
                bg: 'bg-green-100'
              },
              {
                icon: Bell,
                title: 'Smart Alerts',
                description: 'Configurable notifications when conditions change — geofence entry, temperature breach, unexpected shock, or delay.',
                color: 'text-orange-600',
                bg: 'bg-orange-100'
              },
              {
                icon: BarChart3,
                title: 'Analytics & Reporting',
                description: 'Compliance reports, utilization analytics, route optimization insights, and sustainability metrics.',
                color: 'text-purple-600',
                bg: 'bg-purple-100'
              },
              {
                icon: ScanBarcode,
                title: 'QR Scan Activation',
                description: 'Scan device QR + pallet barcode to instantly activate tracking. No manual data entry needed.',
                color: 'text-cyan-600',
                bg: 'bg-cyan-100'
              },
              {
                icon: Globe,
                title: 'Open Integration',
                description: 'REST API, ThingsBoard forwarding, webhook alerts. Integrate with your existing ERP, WMS, or TMS.',
                color: 'text-slate-600',
                bg: 'bg-slate-100'
              }
            ].map((feature) => (
              <div 
                key={feature.title}
                className="p-6 bg-white rounded-2xl border border-slate-200 hover:shadow-lg transition-all hover:-translate-y-1"
              >
                <div className={`w-14 h-14 rounded-xl ${feature.bg} flex items-center justify-center mb-4`}>
                  <feature.icon className={`w-7 h-7 ${feature.color}`} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section id="industries" className="py-20 px-4 bg-gradient-to-br from-blue-600 to-cyan-500">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Built for Your Industry
            </h2>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto">
              SmartPallet adapts to the unique requirements of each value chain
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Seafood / Cold Chain */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all">
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center mb-4">
                <Waves className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Seafood & Cold Chain</h3>
              <p className="text-blue-100 mb-4">
                Continuous temperature monitoring for salmon, fish, and perishable goods. 
                Ensure HACCP compliance from sea to shelf.
              </p>
              <ul className="space-y-2">
                {['Temperature monitoring every 5 min', 'Cold chain compliance reports', 'Automatic breach alerts', 'Origin-to-destination traceability'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-blue-50">
                    <CheckCircle className="w-4 h-4 text-green-300 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-4 border-t border-white/10">
                <span className="text-xs text-blue-200 font-medium uppercase tracking-wider">Active customer: Circmar</span>
              </div>
            </div>

            {/* Consumer Appliances */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all">
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center mb-4">
                <Package className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Electronics & High-Value Goods</h3>
              <p className="text-blue-100 mb-4">
                Track electronics, machinery, and sensitive equipment from warehouse to doorstep. 
                Shock detection protects against handling damage.
              </p>
              <ul className="space-y-2">
                {['Shock & impact detection', 'Proof of delivery location', 'Damage accountability', 'Customer delivery ETA'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-blue-50">
                    <CheckCircle className="w-4 h-4 text-green-300 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-4 border-t border-white/10">
                <span className="text-xs text-blue-200 font-medium uppercase tracking-wider">Use case: Electronics shipment</span>
              </div>
            </div>

            {/* Industrial / Returnable Assets */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all">
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center mb-4">
                <Truck className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Returnable Asset Pools</h3>
              <p className="text-blue-100 mb-4">
                Manage pools of reusable pallets, containers, and crates. 
                Full lifecycle visibility with GS1 GRAI identification.
              </p>
              <ul className="space-y-2">
                {['GS1 GRAI asset identification', 'Pool utilization analytics', 'Idle asset detection', 'Loss & theft prevention'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-blue-50">
                    <CheckCircle className="w-4 h-4 text-green-300 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-6 pt-4 border-t border-white/10">
                <span className="text-xs text-blue-200 font-medium uppercase tracking-wider">Active customer: Smart Retur</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Asset Owners / Pallet Pool Section */}
      <section id="pallet-pool" className="py-20 px-4 bg-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 text-orange-400 rounded-full text-sm font-medium mb-6">
                <Building2 className="w-4 h-4" />
                For Asset Pool Operators
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Turn Your Asset Pool Into a Smart Logistics Service
              </h2>
              <p className="text-xl text-slate-300 mb-8">
                Whether you manage pallets, containers, or specialized equipment — 
                add IoT tracking to differentiate your service and create new revenue streams.
              </p>

              <div className="space-y-6 mb-8">
                {[
                  {
                    title: 'Premium Differentiation',
                    desc: 'Stand out with IoT-enabled assets that customers can track in real time'
                  },
                  {
                    title: 'New Revenue Streams',
                    desc: 'Offer tracking-as-a-service on top of your asset pool business'
                  },
                  {
                    title: 'Full Asset Visibility',
                    desc: 'Know where every asset is — reduce loss, theft, and idle time'
                  },
                  {
                    title: 'Customer Self-Service',
                    desc: 'Give your customers their own dashboard to track shipments and assets'
                  },
                  {
                    title: 'Sustainability Reporting',
                    desc: 'Measure and report on reuse cycles, CO2 savings, and waste reduction'
                  }
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-white">{item.title}</h4>
                      <p className="text-slate-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Button 
                onClick={() => setIsInterestDialogOpen(true)}
                size="lg"
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-lg"
                data-testid="register-interest-btn"
              >
                Register Interest
                <Send className="w-5 h-5 ml-2" />
              </Button>
            </div>

            <div className="bg-slate-800 rounded-3xl p-8 border border-slate-700">
              <h3 className="text-2xl font-bold text-white mb-6">How It Works</h3>
              
              <div className="space-y-6">
                {[
                  {
                    step: '1',
                    title: 'Attach IoT Trackers',
                    desc: 'Small, battery-powered trackers attach to any asset — pallets, crates, containers, or equipment'
                  },
                  {
                    step: '2',
                    title: 'Connect to Platform',
                    desc: 'Devices auto-register via the SILICA IoT cloud. Integrate with your systems via API'
                  },
                  {
                    step: '3',
                    title: 'Scan & Activate',
                    desc: 'Scan device QR + asset barcode to link and start tracking instantly'
                  },
                  {
                    step: '4',
                    title: 'Monitor & Grow',
                    desc: 'Real-time dashboard for you and your customers. Analytics to optimize operations'
                  }
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white font-bold flex-shrink-0">
                      {item.step}
                    </div>
                    <div>
                      <h4 className="font-bold text-white">{item.title}</h4>
                      <p className="text-slate-400 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
                <p className="text-blue-300 text-sm">
                  <strong>Currently onboarding:</strong> Smart Retur (returnable pallets) and 
                  Circmar (seafood cold chain) — contact us to join the pilot.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-slate-950">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <LogoIcon size={40} />
              <span className="text-xl font-bold text-white">SmartPallet</span>
            </div>
            <div className="flex items-center gap-6 text-slate-400 text-sm">
              <span>&copy; 2026 SmartPallet</span>
              <button className="hover:text-white">Privacy</button>
              <button className="hover:text-white">Terms</button>
              <button 
                onClick={() => navigate('/login')}
                className="hover:text-white flex items-center gap-1"
              >
                <Lock className="w-3 h-3" />
                Login
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Interest Dialog */}
      <Dialog open={isInterestDialogOpen} onOpenChange={setIsInterestDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-orange-600" />
              Register Interest
            </DialogTitle>
            <DialogDescription>
              Tell us about your asset tracking needs
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInterestSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                placeholder="Company Name"
                value={interestForm.company}
                onChange={(e) => setInterestForm({...interestForm, company: e.target.value})}
                required
              />
              <Input
                placeholder="Contact Person"
                value={interestForm.contact}
                onChange={(e) => setInterestForm({...interestForm, contact: e.target.value})}
                required
              />
            </div>
            <Input
              type="email"
              placeholder="Email Address"
              value={interestForm.email}
              onChange={(e) => setInterestForm({...interestForm, email: e.target.value})}
              required
            />
            <Input
              placeholder="Estimated asset count"
              value={interestForm.palletCount}
              onChange={(e) => setInterestForm({...interestForm, palletCount: e.target.value})}
            />
            <textarea
              className="w-full p-3 border border-slate-200 rounded-lg text-sm resize-none"
              rows={3}
              placeholder="Tell us about your needs..."
              value={interestForm.message}
              onChange={(e) => setInterestForm({...interestForm, message: e.target.value})}
            />
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Interest'
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Landing;
